import { FC, useEffect, useState } from 'react';
import { Palette } from '@updater/ui-uds';
import {
  AccentPlanCard,
  AccentPlanCardTopperProps,
  PlanDataProvider,
  PlanTermToggle,
  Showcase,
} from '@updater/ui-widgets';
import { useTracking } from 'react-tracking';
import { useFeatureValue } from '@growthbook/growthbook-react';
import type {
  ServiceabilityProvider,
  TvInternetOffer,
} from '@updater/consumer-graph';
import { getProviderConfig } from 'config';
import { OfferPair } from 'hooks/useTermGroupedOffers';
import {
  getTermPartner,
  pickTermEntry,
  useTermSelection,
} from 'hooks/useTermSelection';
import {
  getShowcaseToppers,
  PILL_TOPPER_RULES,
  ResolvedTopper,
  TopperRule,
} from 'utils/topper-logic';
import {
  SortPill,
  SORT_PILL_SECTION_TITLES,
  SKIPPED_QUIZ_RECOMMENDED_TITLE,
} from 'constants/sortPills';
import type { SortMethod } from '@updater/ui-widgets';
import {
  OfferListSection,
  toOfferImpression,
  type MerchandisingContext,
  type OfferImpression,
} from '@updater/ui-utilities/merchandising-context';
import { AccentPlanCardSummary } from 'components/PlanCard/AccentPlanCardSummary';
import { AccentPlanCardDetailsModal } from './AccentPlanCardDetailsModal';

export type AccentShowcaseSectionProps = {
  offerPairs: OfferPair[];
  /** Undefined until the quiz answers are read; empty if it was skipped. */
  quizInternetUsage: string[] | undefined;
  activePill?: SortPill;
  requestId: string;
  onClickSelectButton: (
    params: {
      offer: TvInternetOffer;
      provider: ServiceabilityProvider;
      isZipOnlyAddress: boolean;
      alreadyTracked?: boolean;
      clickOrderPosition?: number;
    } & MerchandisingContext<SortMethod>
  ) => void;
  isZipOnlyAddress: boolean;
  /** Fingerprint of the page layout, from the impression; passed to every pick. */
  orderingHash?: string;
  /** Receives the offers this row actually renders, in order, for the impression. */
  onRenderedOffers?: (offers: OfferImpression[]) => void;
  /** Runs before a pick is tracked, so the impression it came from lands first. */
  onBeforeSelect?: () => void;
};

export const PILL_PRIMARY_RULES: Partial<Record<SortPill, TopperRule>> = {
  [SortPill.Speed]: PILL_TOPPER_RULES.fastest,
  [SortPill.BestValue]: PILL_TOPPER_RULES.bestValue,
  [SortPill.Price]: PILL_TOPPER_RULES.price,
};

function resolveTopper(topper: ResolvedTopper): AccentPlanCardTopperProps {
  const iconColor =
    topper.variant === 'primary' ? Palette.white : Palette.mint600;
  return {
    label: topper.label,
    variant: topper.variant,
    icon: <topper.Icon weight="regular" size={24} color={iconColor} />,
  };
}

export const AccentShowcaseSection: FC<AccentShowcaseSectionProps> = ({
  offerPairs,
  requestId,
  quizInternetUsage,
  activePill = SortPill.Recommended,
  onClickSelectButton,
  isZipOnlyAddress,
  orderingHash,
  onRenderedOffers,
  onBeforeSelect,
}) => {
  const [drawerOfferIndex, setDrawerOfferIndex] = useState<number | null>(null);
  const { getTerm, setTerm } = useTermSelection();
  const updaterAttOfferEnabled = useFeatureValue(
    'provider_app_att_new_promos_feb_2026',
    false
  );
  const { trackEvent } = useTracking({ object: 'offers' });

  const primaryRule = PILL_PRIMARY_RULES[activePill];
  const offersWithToppers = getShowcaseToppers(
    offerPairs,
    quizInternetUsage,
    primaryRule
  );

  // Report the rendered row itself, not a recomputation of it, keyed on content
  // so a re-render does not re-report.
  const renderedOffers = offersWithToppers.map((entry) =>
    toOfferImpression(entry.offer, {
      badgeId: entry.topper.id,
      partner: entry.partner?.offer,
    })
  );
  const renderedSignature = JSON.stringify(renderedOffers);
  useEffect(() => {
    onRenderedOffers?.(renderedOffers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedSignature]);

  if (!offersWithToppers.length) return null;

  /**
   * Deliberately carries no `sortMethod`: this row is ordered by the topper
   * comparator and the active pill, never by the sort applied to the list
   * below, so naming that sort here would describe an ordering that never ran.
   */
  const contextFor = (topperId: string): MerchandisingContext<SortMethod> => ({
    section: OfferListSection.Showcase,
    sortPill: activePill,
    badgeId: topperId,
    totalOffersShown: offersWithToppers.length,
    orderingHash,
  });

  // No quiz answers means the toppers above aren't personalized. Until the
  // answers are read, a non-breaking space holds the heading's line, so the
  // wrong title never flashes and nothing shifts when the right one lands.
  const sectionTitle =
    activePill !== SortPill.Recommended
      ? SORT_PILL_SECTION_TITLES[activePill]
      : quizInternetUsage === undefined
      ? '\u00a0'
      : quizInternetUsage.length === 0
      ? SKIPPED_QUIZ_RECOMMENDED_TITLE
      : SORT_PILL_SECTION_TITLES[activePill];

  const selectedPick =
    drawerOfferIndex !== null ? offersWithToppers[drawerOfferIndex] : null;
  // The entry the modal shows: the term its toggle is on, kept in the same
  // shared state the card behind it reads.
  const selectedEntry = selectedPick
    ? pickTermEntry(selectedPick, getTerm(selectedPick.offer.code))
    : null;
  const selectedEntryProviderConfig = selectedEntry
    ? getProviderConfig({ providerCode: selectedEntry.provider.code })
    : null;

  return (
    <>
      <Showcase
        bleed={16}
        title={sectionTitle}
        offers={offersWithToppers}
        keyExtractor={(entry) => `${entry.offer.code ?? ''}-${entry.topper.id}`}
        renderOffer={(entry, index) => {
          const providerConfig = getProviderConfig({
            providerCode: entry.provider.code,
          });

          return (
            <PlanDataProvider
              offer={entry.offer}
              provider={entry.provider}
              planViewData={{
                broadbandDisplayType: providerConfig.broadbandDisplayType,
                updaterAttOfferEnabled,
                disclaimerDisplaySettings:
                  providerConfig.disclaimerDisplaySettings,
                showTaxesAndFees: providerConfig.showTaxesAndFees,
              }}
            >
              <AccentPlanCard
                data-testid="offerCard"
                height="100%"
                mb="none"
                summary={<AccentPlanCardSummary />}
                topper={resolveTopper(entry.topper)}
                maxWidth={944}
                termPair={entry}
                selectedTerm={getTerm(entry.offer.code)}
                onTermChange={(next) => setTerm(entry.offer.code, next)}
                // Both callbacks take the entry the card is displaying, so a
                // toggled 5-year offer rides through instead of the 1-year
                // offer this closure would otherwise capture.
                onClickViewDetails={(selected) => {
                  trackEvent({
                    object: 'view_details',
                    verb: 'clicked',
                    details: {
                      offer: selected.offer,
                      provider: selected.provider,
                      position: index,
                    },
                  });
                  setDrawerOfferIndex(index);
                }}
                onClickSelectButton={(selected) => {
                  onBeforeSelect?.();
                  // This is the only event a showcase pick emits: the call
                  // below passes `alreadyTracked`, so `offers.submitted` never
                  // fires and cannot carry the context for us.
                  trackEvent({
                    object: 'select_topper_offer',
                    verb: 'clicked',
                    details: {
                      offer: selected.offer,
                      provider: selected.provider,
                      requestId,
                      position: index,
                      topperName: entry.topper.label,
                      ...contextFor(entry.topper.id),
                    },
                  });
                  onClickSelectButton({
                    offer: selected.offer,
                    provider: selected.provider,
                    isZipOnlyAddress,
                    alreadyTracked: true,
                    clickOrderPosition: index,
                    ...contextFor(entry.topper.id),
                  });
                }}
              />
            </PlanDataProvider>
          );
        }}
      />

      {selectedPick && selectedEntry && (
        <PlanDataProvider
          offer={selectedEntry.offer}
          provider={selectedEntry.provider}
          planViewData={{
            broadbandDisplayType:
              selectedEntryProviderConfig?.broadbandDisplayType,
            updaterAttOfferEnabled,
            disclaimerDisplaySettings:
              selectedEntryProviderConfig?.disclaimerDisplaySettings,
            showTaxesAndFees: selectedEntryProviderConfig?.showTaxesAndFees,
          }}
        >
          <AccentPlanCardDetailsModal
            isOpen={drawerOfferIndex !== null}
            onClose={() => setDrawerOfferIndex(null)}
            // Same shared term state the card reads, so the modal and the card
            // behind it always agree. No partner means no toggle to show.
            termToggle={
              getTermPartner(selectedPick) ? (
                <PlanTermToggle
                  value={getTerm(selectedPick.offer.code)}
                  onChange={(next) => setTerm(selectedPick.offer.code, next)}
                />
              ) : undefined
            }
            onSelectPlan={() => {
              onBeforeSelect?.();
              trackEvent({
                object: 'select_details_offer',
                verb: 'clicked',
                details: {
                  offer: selectedEntry.offer,
                  provider: selectedEntry.provider,
                  requestId,
                  position: drawerOfferIndex,
                  ...contextFor(selectedPick.topper.id),
                },
              });
              setDrawerOfferIndex(null);
              onClickSelectButton({
                offer: selectedEntry.offer,
                provider: selectedEntry.provider,
                isZipOnlyAddress,
                alreadyTracked: true,
                clickOrderPosition: drawerOfferIndex ?? undefined,
                ...contextFor(selectedPick.topper.id),
              });
            }}
          />
        </PlanDataProvider>
      )}
    </>
  );
};
